import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { CheckCircle2 } from "lucide-react";

const AUTH_SUFFIX = "/V3/API/Auth/Usuario";

const deriveAuthUrl = (baseUrl: string) => `${baseUrl.replace(/\/$/, "")}${AUTH_SUFFIX}`;

export type SincoCredentials = {
  authUrl: string;
  baseUrl: string;
  nomUsuario: string;
  claveUsuario: string;
};

export type ExistingConnection = {
  accessToken: string;
  baseUrl: string;
  authUrl: string;
  nomUsuario: string;
};

type Props = {
  initialValues?: Partial<SincoCredentials>;
  existingConnection?: ExistingConnection | null;
  onSubmit: (credentials: SincoCredentials) => void;
  onContinueWithStored?: (data: ExistingConnection) => void;
  isLoading?: boolean;
  isLoadingStoredCheck?: boolean;
};

const getInitialBaseUrl = (initial?: Partial<SincoCredentials>): string => {
  if (initial?.baseUrl) return initial.baseUrl;
  if (initial?.authUrl) return initial.authUrl.replace(/\/V3\/API\/Auth\/Usuario\/?$/i, "");
  return "";
};

const SincoCredentialsForm = ({
  initialValues,
  existingConnection,
  onSubmit,
  onContinueWithStored,
  isLoading,
  isLoadingStoredCheck,
}: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const [baseUrl, setBaseUrl] = useState(getInitialBaseUrl(initialValues));
  const [nomUsuario, setNomUsuario] = useState(initialValues?.nomUsuario ?? "");
  const [claveUsuario, setClaveUsuario] = useState(initialValues?.claveUsuario ?? "");

  const showSuccessState = existingConnection && !isEditing && !isLoadingStoredCheck;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      baseUrl,
      authUrl: deriveAuthUrl(baseUrl),
      nomUsuario,
      claveUsuario,
    });
  };

  const handleEdit = () => {
    if (existingConnection) {
      setBaseUrl(existingConnection.baseUrl);
      setNomUsuario(existingConnection.nomUsuario);
      setClaveUsuario("");
    }
    setIsEditing(true);
  };

  if (isLoadingStoredCheck) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Conexión SINCO</CardTitle>
          <CardDescription>Verificando credenciales configuradas...</CardDescription>
        </CardHeader>
        <CardContent className="flex min-h-[200px] items-center justify-center">
          <Spinner size="lg" />
        </CardContent>
      </Card>
    );
  }

  if (showSuccessState && existingConnection) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Conexión SINCO</CardTitle>
          <CardDescription>
            Las credenciales configuradas son válidas. Puedes continuar o editarlas si lo necesitas.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-4 text-green-800">
            <CheckCircle2 className="size-6 shrink-0 text-green-600" />
            <span className="font-medium">Conexión exitosa</span>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => onContinueWithStored?.(existingConnection)}>Continuar</Button>
            <Button variant="outline" onClick={handleEdit}>
              Editar credenciales
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Conexión SINCO</CardTitle>
        <CardDescription>
          Ingresa la URL base y credenciales para conectarte al API de SINCO.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="space-y-2">
            <Label htmlFor="baseUrl">URL base</Label>
            <Input
              id="baseUrl"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://www1.sincoerp.com/SincoVerticeIng"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="nomUsuario">Usuario</Label>
            <Input
              id="nomUsuario"
              value={nomUsuario}
              onChange={(e) => setNomUsuario(e.target.value)}
              placeholder="APICBR"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="claveUsuario">Clave</Label>
            <Input
              id="claveUsuario"
              type="password"
              value={claveUsuario}
              onChange={(e) => setClaveUsuario(e.target.value)}
              placeholder="Clave encriptada"
              required
            />
          </div>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Conectando..." : "Conectar"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default SincoCredentialsForm;
